import { useEffect, useState } from "react";
import {
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import conf from "config-dlm";
import Architecture from "./grace.svg";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";

const GENERAL = [
  {
    key: "pavm",
    type: "service",
    description:
      "Project Appliance Virtual Machine; a hosted service running in AWS/Fargate used to connect to specific DLM project/customer",
  },
  {
    key: "s-comm",
    type: "service",
    description:
      "Public/external facing GRACE service running on host/appliance (directly reachable by PAVMs)",
  },
  {
    key: "s-stat",
    type: "service",
    description: (
      <>
        Private/internal facing GRACE service running on host/appliance (
        <b>not</b> directly reachable by PAVMs)
      </>
    ),
  },
  {
    key: "listen",
    type: "service",
    description:
      "Systemd service running directly on appliance; used to manage IPC between appliance & containerized services (note: exclusive to Rocky)",
  },
  {
    key: "role",
    type: "primary | standby | standalone",
    description: "String representing current HA status of host/appliance",
  },
  {
    key: "location",
    type: "string",
    description:
      "String representing location of host/appliance (note: if hosted, will display 'AWS/Cloud')",
  },
  {
    key: "hardware",
    type: "virtual | physical",
    description:
      "String representing whether host/appliance is virtual (cloud, aws, etc.) or physical (bare metal, data center, etc.)",
  },
  {
    key: "in-docker",
    type: "true | false",
    description:
      "Boolean representing whether ISS Suport Portal services (S-COMM/S-STAT) are running directly on host/appliance or in container via Docker",
  },
  {
    key: "Last updated",
    type: "sec/min/hrs",
    description:
      "Live timer/clock of data freshness; will dynamically calculate time since data was last fetched. (note: will present error if host is unreachable)",
  },
];

const COMMANDS = [
  {
    key: "WHICH_CTCONF",
    command: "which ctconf",
    description: "find ctconf binary",
  },
  {
    key: "WHICH_CKTOOL",
    command: "which cktool",
    description: "find cktool binary",
  },
  {
    key: "WHICH_CKLIST",
    command: "which cklist",
    description: "find cklist binary",
  },
  {
    key: "WHICH_CKDUMP",
    command: "which ckdump",
    description: "find ckdump binary",
  },
  {
    key: "WHICH_CCCONF",
    command: "which ccconf",
    description: "find ccconf binary",
  },
  {
    key: "WHICH_SENSORS",
    command: "which sensors",
    description: "find sensors binary",
  },
  {
    key: "WHICH_IPMI_OEM",
    command: "which ipmi-oem",
    description: "find ipmi-oem binary",
  },
  {
    key: "LISTEN_PORT",
    command: "pgrep listen",
    description: `get listen service active port no.`,
  },
  {
    key: "LISTEN_INFO",
    command: "ps aux | grep listen.sh | grep -v grep",
    description: `get listen service full info (port, script location, etc.)`,
  },
  {
    key: "LISTEN_STATUS",
    command: "systemctl status listen",
    description: `get listen service status (full)`,
  },
  {
    key: "LISTEN_STATUS_ONLY_ACTIVE",
    command: "systemctl status listen | grep Active",
    description: `get listen service status (only 'active' name)`,
  },
  {
    key: "HSM_CTCONF_V",
    command: "ctconf -v",
    description: `get hsm general info (centos)`,
  },
  {
    key: "HSM_CKTOOL_ID_J",
    command: "cktool id -j",
    description: `get hsm general info (rocky, JSON)`,
  },
  {
    key: "HSM_CCCONF_CLOCK",
    command: "CCconf query=ciUtcDateTime",
    description: `get hsm date/time info (centos)`,
  },
  {
    key: "HOST_DISK",
    command: "df -h",
    description: `get host disk info`,
  },
  {
    key: "HOST_CLOCK",
    command: "date",
    description: `get host clock info`,
  },
  {
    key: "HOST_KERNEL",
    command: "uname -r",
    description: `get host kernel version`,
  },
  {
    key: "HOST_ROLE",
    command: "cat /etc/vcoreserver/ha-status.conf",
    description: `get host ha status/role`,
  },
  {
    key: "HOST_SERIAL_NO",
    command: "sudo dmidecode -s system-serial-number",
    description: `get host serial number`,
  },
  {
    key: "HOST_MEMORY",
    command: 'awk "/^Mem[Total|Free|Avail]/" /proc/meminfo',
    description: `get host memory info (total, free, available)`,
  },
  {
    key: "HOST_CPU_USAGE",
    command: 'vmstat 1 2 | tail -1 | awk "{print $15}"',
    description: `get host cpu usage`,
  },
  {
    key: "HOST_CPU_TEMP",
    command: `sensors | grep Package | awk '{print $4}' | grep . || sensors | grep Physical | awk '{print $4}' | grep . || sensors | grep Core | awk '{print $3}' | grep . || sensors | grep temp | awk '{print $2}' | grep . || cat /sys/class/thermal/thermal_zone*/temp 2>/dev/null | awk '{print $1/1000}'`,
    description: `get host cpu temperature ('sensor' pkg must be installed)`,
  },
  {
    key: "HOST_FAN_MODE",
    command:
      'sudo /usr/local/bin/IPMICFG-Linux.x86_64 -fan | grep -Po "\\[\\K[^]]*" | xargs',
    description: `get host fan mode`,
  },
  {
    key: "HOST_POWER_SUPPLY_1",
    command: "sudo ipmi-oem Supermicro get-power-supply-status 1",
    description: `get host power supply 1 info`,
  },
  {
    key: "HOST_POWER_SUPPLY_2",
    command: "sudo ipmi-oem Supermicro get-power-supply-status 2",
    description: `get host power supply 2 info`,
  },
  {
    key: "HOST_VERSION",
    command: "sudo dmidecode -s system-version",
    description: `get host version`,
  },
  {
    key: "HOST_PRODUCT_NAME",
    command: "sudo dmidecode -s system-product-name",
    description: `get host product name`,
  },
];

const FUTURE = [
  {
    feature: "SELF UPDATE",
    priority: 1,
    description:
      "Appliance-based services (s-stat & s-comm) would have the ability to self-update; ie. receive & install updated versions",
  },
  {
    feature: "SSH SHELL",
    priority: 2,
    description:
      "To troubleshoot issues further, a user could optionally open a live SSH session for a particular appliance (limited access/permissions)",
  },
];

const GeneralTable = () => (
  <TableRow
    sx={{
      bgcolor: (theme) =>
        theme.palette.mode === "light" ? "primary.light" : "primary.dark",
    }}
  >
    <TableCell colSpan={4}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell>
              <b>Keyword</b>
            </TableCell>
            <TableCell>
              <b>Type / Value</b>
            </TableCell>
            <TableCell>
              <b>Description</b>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {GENERAL.map(
            (spec: {
              key: string;
              type: string;
              description: string | JSX.Element;
            }) => (
              <TableRow key={spec.key} sx={conf.LAST_CHILD_NO_BORDER}>
                <TableCell>
                  <b>{spec.key}</b>
                </TableCell>
                <TableCell>
                  <code>{spec.type}</code>
                </TableCell>
                <TableCell>{spec.description}</TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableCell>
  </TableRow>
);

const CommandsTable = () => (
  <TableRow
    sx={{
      bgcolor: (theme) =>
        theme.palette.mode === "light" ? "primary.light" : "primary.dark",
    }}
  >
    <TableCell colSpan={4}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell>
              <b>Key</b>
            </TableCell>
            <TableCell>
              <b>Command</b>
            </TableCell>
            <TableCell>
              <b>Description</b>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {COMMANDS.map(
            (spec: { key: string; command: string; description: string }) => (
              <TableRow key={spec.key} sx={conf.LAST_CHILD_NO_BORDER}>
                <TableCell>{spec.key}</TableCell>
                <TableCell>
                  <code>{spec.command.replace(/^sudo\s/, "")}</code>
                </TableCell>
                <TableCell>{spec.description}</TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableCell>
  </TableRow>
);

const Diagram = () => (
  <TableRow
    sx={{
      bgcolor: (theme) =>
        theme.palette.mode === "light" ? "primary.light" : "primary.dark",
    }}
  >
    <TableCell colSpan={4}>
      <img src={Architecture} height={"auto"} width={"100%"} />
    </TableCell>
  </TableRow>
);

const FutureTable = () => (
  <TableRow
    sx={{
      bgcolor: (theme) =>
        theme.palette.mode === "light" ? "primary.light" : "primary.dark",
    }}
  >
    <TableCell colSpan={4}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell>
              <b>Feature</b>
            </TableCell>
            <TableCell>
              <b>Priority</b>
            </TableCell>
            <TableCell>
              <b>Description</b>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {FUTURE.map(
            (spec: {
              feature: string;
              priority: number;
              description: string;
            }) => (
              <TableRow key={spec.feature} sx={conf.LAST_CHILD_NO_BORDER}>
                <TableCell>
                  <b>{spec.feature}</b>
                </TableCell>
                <TableCell>
                  <code>{spec.priority}</code>
                </TableCell>
                <TableCell>{spec.description}</TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableCell>
  </TableRow>
);

const BaseTable = () => {
  const [openGeneral, setOpenGeneral] = useState(false);
  const toggleGeneral = () => setOpenGeneral(!openGeneral);

  const [openDiagram, setOpenDiagram] = useState(false);
  const toggleDiagram = () => setOpenDiagram(!openDiagram);

  const [openCommands, setOpenCommands] = useState(false);
  const toggleCommands = () => setOpenCommands(!openCommands);

  const [openFuture, setOpenFuture] = useState(false);
  const toggleFuture = () => setOpenFuture(!openFuture);

  return (
    <TableContainer sx={{ boxShadow: 4, maxHeight: 630 }}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>
              <b>Category</b>
            </TableCell>
            <TableCell align="right">
              <b>Description</b>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/* GENERAL ROW */}
          <TableRow sx={conf.LAST_CHILD_NO_BORDER}>
            <TableCell>
              <IconButton
                size="small"
                onClick={toggleGeneral}
                aria-label="toggle detail row"
                sx={{
                  transform: openGeneral ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.3s",
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </TableCell>

            <TableCell>General</TableCell>
            <TableCell align="right">
              General, high-level explanation of key terms, values, & components
              used within GRACE
            </TableCell>
          </TableRow>

          {openGeneral && <GeneralTable />}

          {/* COMMANDS ROW */}
          <TableRow sx={conf.LAST_CHILD_NO_BORDER}>
            <TableCell>
              <IconButton
                size="small"
                onClick={toggleCommands}
                aria-label="toggle detail row"
                sx={{
                  transform: openCommands ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.3s",
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </TableCell>

            <TableCell>Commands</TableCell>
            <TableCell align="right">
              Library of underlying commands leveraged by host services to pull
              appliance data for GRACE
            </TableCell>
          </TableRow>

          {openCommands && <CommandsTable />}

          {/* DIAGRAM ROW */}
          <TableRow sx={conf.LAST_CHILD_NO_BORDER}>
            <TableCell>
              <IconButton
                size="small"
                onClick={toggleDiagram}
                aria-label="toggle detail row"
                sx={{
                  transform: openDiagram ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.3s",
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </TableCell>

            <TableCell>Architecture</TableCell>
            <TableCell align="right">
              Basic, high-level diagram illustrating application/service
              architecture; shows all functional components of GRACE
            </TableCell>
          </TableRow>

          {openDiagram && <Diagram />}

          {/* FUTURE ROW */}
          <TableRow sx={conf.LAST_CHILD_NO_BORDER}>
            <TableCell>
              <IconButton
                size="small"
                onClick={toggleFuture}
                aria-label="toggle detail row"
                sx={{
                  transform: openFuture ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.3s",
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </TableCell>

            <TableCell>Future / WIP</TableCell>
            <TableCell align="right">
              List of future, work-in-progress features yet to be integrated
              into GRACE
            </TableCell>
          </TableRow>

          {openFuture && <FutureTable />}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const Docs = () => {
  useEffect(() => {
    document.title = "GRACE | docs";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl">
        <SectionHeader title="Docs" icon={<AssignmentIcon />} />

        <BaseTable />
      </Container>
    </ProtectedRoute>
  );
};
